'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ClearableSelect } from '../../components/ClearableSelect';
import { useTableFilters } from '../useTableFilters';
import type { ComponentProps } from 'react';
import type { ClearableSelect as ClearableSelectComponent } from '../../components/ClearableSelect';
import type { BaseTableFilterProps, FilterOption } from './types';
export interface SelectTableFilterProps extends BaseTableFilterProps {
  filterOptions: readonly FilterOption[];
  /**
   * Widens `sanitizeSearchParams`'s URL allow-list beyond `filterOptions`, for a dropdown
   * narrowed to a subset of a shared enum (e.g. the institutions that can appear on one
   * table, out of every institution the schema knows about). Without it, a URL carrying a
   * real-but-undisplayed value gets silently dropped — reverting to the unfiltered dataset —
   * instead of correctly filtering to zero rows.
   */
  validValues?: readonly string[];
  searchParam: string;
  variant: 'select';
  componentProps?: Partial<ComponentProps<typeof ClearableSelectComponent>>;
  placeholder?: string;
  /** When true, adds the placeholder text as the first option to allow clearing the filter */
  clearable?: boolean;
  /** Custom renderer for option labels in the dropdown (e.g., colored badges) */
  renderLabel?: (option: FilterOption) => React.ReactNode;
  groups?: never;
  value?: never;
  fromDateParam?: never;
  toDateParam?: never;
}
export function SelectTableFilter(t0) {
  const $ = _c(31);
  const {
    className,
    label,
    searchParam,
    searchParamsToClearOnChange,
    componentProps,
    filterOptions,
    validValues,
    placeholder,
    clearable: t1,
    renderLabel,
    filterType: t2
  } = t0;
  const clearable = t1 === undefined ? false : t1;
  const filterType = t2 === undefined ? "rsc" : t2;
  const searchParams = useSearchParams();
  const {
    updateFilterValue
  } = useTableFilters(filterType);
  const [selectedValue, setSelectedValue] = useState(placeholder ? "" : filterOptions?.[0]?.value as string || "");
  let t3;
  if ($[0] !== filterOptions?.[0]?.value || $[1] !== placeholder || $[2] !== searchParam || $[3] !== searchParams) {
    t3 = () => {
      const searchParamValue = searchParams?.get(searchParam);
      if (searchParamValue) {
        setSelectedValue(searchParamValue);
      } else {
        if (placeholder) {
          setSelectedValue("");
        } else {
          setSelectedValue(filterOptions?.[0]?.value as string || "");
        }
      }
    };
    $[0] = filterOptions?.[0]?.value;
    $[1] = placeholder;
    $[2] = searchParam;
    $[3] = searchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== filterOptions || $[6] !== placeholder || $[7] !== searchParam || $[8] !== searchParams) {
    t4 = [searchParams, searchParam, filterOptions, placeholder];
    $[5] = filterOptions;
    $[6] = placeholder;
    $[7] = searchParam;
    $[8] = searchParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== searchParam || $[11] !== searchParamsToClearOnChange || $[12] !== updateFilterValue) {
    t5 = function handleChange(value) {
      const stringValue = value ?? "";
      setSelectedValue(stringValue);
      updateFilterValue(searchParam, stringValue, searchParamsToClearOnChange);
    };
    $[10] = searchParam;
    $[11] = searchParamsToClearOnChange;
    $[12] = updateFilterValue;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleChange = t5;
  if (!filterOptions) {
    return null;
  }
  let isDisplayable;
  let t6;
  if ($[14] !== filterOptions || $[15] !== selectedValue || $[16] !== validValues) {
    const options = filterOptions.map(_temp);
    const isAllowedButUndisplayed = Boolean(selectedValue) && (validValues?.includes(selectedValue) ?? false);
    isDisplayable = isAllowedButUndisplayed || options.some(o_0 => o_0.value === selectedValue);
    t6 = isAllowedButUndisplayed && !options.some(o_1 => o_1.value === selectedValue) ? [...options, {
      value: selectedValue,
      label: selectedValue
    }] : options;
    $[14] = filterOptions;
    $[15] = selectedValue;
    $[16] = validValues;
    $[17] = isDisplayable;
    $[18] = t6;
  } else {
    isDisplayable = $[17];
    t6 = $[18];
  }
  const selectOptions = t6;
  const t7 = label ?? placeholder ?? searchParam;
  let t8;
  if ($[19] !== renderLabel) {
    t8 = renderLabel ? o_2 => renderLabel({
      value: o_2.value,
      label: o_2.label
    }) : undefined;
    $[19] = renderLabel;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = isDisplayable ? selectedValue || null : null;
  let t10;
  if ($[21] !== className || $[22] !== clearable || $[23] !== componentProps || $[24] !== handleChange || $[25] !== placeholder || $[26] !== selectOptions || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <ClearableSelect className={className} ariaLabel={t7} clearable={clearable} onChange={handleChange} options={selectOptions} placeholder={placeholder} renderLabel={t8} value={t9} {...componentProps} />;
    $[21] = className;
    $[22] = clearable;
    $[23] = componentProps;
    $[24] = handleChange;
    $[25] = placeholder;
    $[26] = selectOptions;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}
function _temp(o) {
  return {
    value: o.value as string,
    label: o.label
  };
}